import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router'
import { Toaster } from 'react-hot-toast'
import { useEffect, lazy, Suspense } from 'react'
import type { ReactNode } from 'react'
import './App.css'
import { trackPageView, trackDataFastPageView } from './lib/analytics'
import { useCookieConsent } from './contexts/CookieConsentContext'
import { useAuth } from './hooks/useAuth'
import { PortfolioProvider } from './contexts/PortfolioContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { DashboardProvider } from './contexts/DashboardContext'
import SignUpPage from './pages/SignUpPage'
import SignUpSuccessPage from './pages/SignUpSuccessPage'
import SignInPage from './pages/SignInPage'
import PasswordResetPage from './pages/PasswordResetPage'
import AuthCallbackPage from './pages/AuthCallbackPage'
import NotFoundPage from './pages/NotFoundPage'
import CookieBanner from './components/common/CookieBanner'
import CookiePreferencesModal from './components/common/CookiePreferencesModal'
import SecondChanceConsentPrompt from './components/common/SecondChanceConsentPrompt'
import FunnelMeasurementRelay from './components/common/FunnelMeasurementRelay'
import PageLoader from './components/common/PageLoader'
import { useSecondChanceConsent } from './hooks/useSecondChanceConsent'
import { AffiliateAttributionManager } from './components/affiliate/AffiliateAttributionManager'
import { captureFirstTouch, captureLandingViewedOnce } from './lib/signupAttribution'
import { hasActiveNativeAuthIntent } from './lib/nativeAuthIntent'
import { lazyWithReload } from './utils/lazyWithReload'

import ProtectedRoute from './components/auth/ProtectedRoute'
import CoreAccessRoute from './components/auth/CoreAccessRoute'
import MobileRootRedirect from './components/auth/MobileRootRedirect'
import Navigation from './components/common/Navigation'
import SearchProvider from './search/SearchProvider'
import LegacyFreePlanBanner from './components/common/LegacyFreePlanBanner'
import DevAuthSwitcher from './components/common/DevAuthSwitcher'
import ScrollToTop from './components/common/ScrollToTop'
import { FloatingFeedbackButton } from './components/feedback'
import MobilePushPermissionPrompt from './components/mobile/MobilePushPermissionPrompt'
import { isNativeWelcomeEntryPath, shouldUseNativeWelcome } from './config/nativeWelcome'

// Route implementations load only when that route is selected.
const LandingPage = lazyWithReload(() => import('./pages/LandingPage'), 'LandingPage')
const FAQPage = lazyWithReload(() => import('./pages/FAQPage'), 'FAQPage')
const ContactPage = lazyWithReload(() => import('./pages/ContactPage'), 'ContactPage')
const PrivacyPage = lazyWithReload(() => import('./pages/PrivacyPage'), 'PrivacyPage')
const TermsPage = lazyWithReload(() => import('./pages/TermsPage'), 'TermsPage')
const CookiePolicyPage = lazyWithReload(
  () => import('./pages/CookiePolicyPage'),
  'CookiePolicyPage'
)
const HowToTrackOptionsPage = lazyWithReload(
  () => import('./pages/HowToTrackOptionsPage'),
  'HowToTrackOptionsPage'
)
const OptionsPaperTradingSimulatorPage = lazyWithReload(
  () => import('./pages/OptionsPaperTradingSimulatorPage'),
  'OptionsPaperTradingSimulatorPage'
)
const RefundPolicyPage = lazyWithReload(
  () => import('./pages/RefundPolicyPage'),
  'RefundPolicyPage'
)
const AboutPage = lazyWithReload(() => import('./pages/AboutPage'), 'AboutPage')
const PricingPage = lazyWithReload(() => import('./pages/PricingPage'), 'PricingPage')

// Load authenticated route implementations only when selected.
const SettingsPage = lazy(() => import('./pages/SettingsPage'))
const DashboardPage = lazyWithReload(() => import('./pages/DashboardPage'), 'DashboardPage')
const PortfolioPage = lazyWithReload(() => import('./pages/PortfolioPage'), 'PortfolioPage')
const ScenarioLabPage = lazy(() => import('./pages/ScenarioLabPage'))
const TradeHistoryPage = lazy(() => import('./pages/TradeHistoryPage'))
const YourEdgePage = lazy(() => import('./pages/YourEdgePage'))
const ScreenerPage = lazy(() => import('./pages/ScreenerPage'))
const DiscoveryPage = lazy(() => import('./pages/DiscoveryPage'))
const StrategyHubPage = lazy(() => import('./pages/StrategyHubPage'))
const ComparisonPage = lazy(() => import('./pages/ComparisonPage'))
const ComparisonHubPage = lazy(() => import('./pages/ComparisonHubPage'))
const FeatureHubPage = lazy(() => import('./pages/FeatureHubPage'))
const ProductFeaturePage = lazyWithReload(
  () => import('./pages/ProductFeaturePage'),
  'ProductFeaturePage'
)
const GlossaryPage = lazy(() => import('./pages/GlossaryPage'))
const LearningHubPage = lazyWithReload(() => import('./pages/LearningHubPage'), 'LearningHubPage')
const ScreenerGuidePage = lazy(() => import('./pages/ScreenerGuidePage'))
const DiscoveryGuidePage = lazy(() => import('./pages/DiscoveryGuidePage'))
const RollAssistantGuidePage = lazy(() => import('./pages/RollAssistantGuidePage'))
const ScenarioLabGuidePage = lazy(() => import('./pages/ScenarioLabGuidePage'))
const WheelStrategyPage = lazy(() => import('./pages/WheelStrategyPage'))
const CoveredCallPage = lazy(() => import('./pages/CoveredCallPage'))
const PoorMansCoveredCallPage = lazy(() => import('./pages/PoorMansCoveredCallPage'))
const IronCondorPage = lazy(() => import('./pages/IronCondorPage'))
const MarriedPutPage = lazy(() => import('./pages/MarriedPutPage'))
const BullCallSpreadPage = lazy(() => import('./pages/BullCallSpreadPage'))
const BearPutSpreadPage = lazy(() => import('./pages/BearPutSpreadPage'))
const ProtectiveCollarPage = lazy(() => import('./pages/ProtectiveCollarPage'))
const LongStraddlePage = lazy(() => import('./pages/LongStraddlePage'))
const LongStranglePage = lazy(() => import('./pages/LongStranglePage'))
const LongCallButterflyPage = lazy(() => import('./pages/LongCallButterflyPage'))
const IronButterflyPage = lazy(() => import('./pages/IronButterflyPage'))
const BlogPage = lazy(() => import('./pages/BlogPage'))
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'))
const FeedbackPage = lazy(() => import('./pages/FeedbackPage'))
const AdminScanMonitorPage = lazy(() => import('./pages/AdminScanMonitorPage'))
const AdminEmailCampaignsPage = lazy(() => import('./pages/AdminEmailCampaignsPage'))
const AdminBrokerSyncPage = lazyWithReload(
  () => import('./pages/AdminBrokerSyncPage'),
  'AdminBrokerSyncPage'
)
const SecurityPage = lazy(() => import('./pages/SecurityPage'))
const BrokerSyncPage = lazy(() => import('./pages/BrokerSyncPage'))
const IbkrBrokerSyncPage = lazy(() => import('./pages/IbkrBrokerSyncPage'))
const DataDeletionPage = lazy(() => import('./pages/DataDeletionPage'))
const CSVImportGuidePage = lazy(() => import('./pages/CSVImportGuidePage'))

// SEO Pages
const OptionsProfitCalculatorPage = lazy(() => import('./pages/seo/OptionsProfitCalculatorPage'))
const GlossaryThetaPage = lazy(() => import('./pages/seo/GlossaryThetaPage'))
const DemoPage = lazy(() => import('./pages/DemoPage'))
const UnsubscribePage = lazy(() => import('./pages/UnsubscribePage'))
const UpgradeRequiredPage = lazy(() => import('./pages/UpgradeRequiredPage'))
const TrialActivationPage = lazy(() => import('./pages/TrialActivationPage'))
const AffiliatePage = lazy(() => import('./pages/AffiliatePage'))
const AffiliateDashboardPage = lazyWithReload(
  () => import('./pages/AffiliateDashboardPage'),
  'AffiliateDashboardPage'
)
const AdminAffiliatePage = lazy(() => import('./pages/AdminAffiliatePage'))
const AdminFunnelPage = lazy(() => import('./pages/AdminFunnelPage'))
const TrialFeedbackPage = lazy(() => import('./pages/TrialFeedbackPage'))
const InactiveFeedbackPage = lazy(() => import('./pages/InactiveFeedbackPage'))
const AffiliateReferralRedirectPage = lazy(() => import('./pages/AffiliateReferralRedirectPage'))

function withPageLoader(page: ReactNode) {
  return <Suspense fallback={<PageLoader />}>{page}</Suspense>
}

// Track page views on route change (consent-aware)
function PageTracker() {
  const location = useLocation()
  const { analyticsReady, hasCategory } = useCookieConsent()

  useEffect(() => {
    if (hasCategory('analytics') && location.pathname !== '/inactive-feedback') {
      trackDataFastPageView(location.pathname + location.search)
    }
  }, [location, hasCategory])

  useEffect(() => {
    // Wait for asynchronous GA initialization so the first consented page view is not lost.
    // Signed feedback tokens must never be copied into analytics page URLs.
    if (analyticsReady && hasCategory('analytics') && location.pathname !== '/inactive-feedback') {
      trackPageView(location.pathname + location.search)
    }
  }, [analyticsReady, location, hasCategory])

  return null
}

function FirstTouchAttributionManager() {
  const location = useLocation()
  const { hasCategory } = useCookieConsent()

  useEffect(() => {
    captureFirstTouch()
    if (location.pathname === '/' && hasCategory('analytics')) {
      captureLandingViewedOnce()
    }
  }, [hasCategory, location.pathname])

  return null
}

// Global second-chance consent prompt manager
function SecondChanceConsentManager() {
  const { showPrompt, dismissPrompt, enableAnalytics, triggerPrompt, canShowPrompt } =
    useSecondChanceConsent()

  // Listen for trigger events from CSVExportButton
  useEffect(() => {
    const handleTrigger = () => {
      if (canShowPrompt) {
        triggerPrompt()
      }
    }

    window.addEventListener('optioneer:csv-export-success', handleTrigger)
    return () => window.removeEventListener('optioneer:csv-export-success', handleTrigger)
  }, [canShowPrompt, triggerPrompt])

  return (
    <SecondChanceConsentPrompt
      visible={showPrompt}
      onDismiss={dismissPrompt}
      onEnableAnalytics={enableAnalytics}
      contextMessage="Glad you're using the export feature!"
    />
  )
}

function useSuppressNativeWelcomeChrome() {
  const location = useLocation()
  const { user, loading } = useAuth()

  return (
    shouldUseNativeWelcome() &&
    isNativeWelcomeEntryPath(location.pathname) &&
    (loading || !user || hasActiveNativeAuthIntent())
  )
}

function AppChrome() {
  const location = useLocation()
  const suppressNativeWelcomeChrome = useSuppressNativeWelcomeChrome()

  if (suppressNativeWelcomeChrome || location.pathname === '/inactive-feedback') {
    return null
  }

  return (
    <>
      <AffiliateAttributionManager />
      <Navigation />
      <LegacyFreePlanBanner />
      <DevAuthSwitcher />
      <FloatingFeedbackButton />
      <MobilePushPermissionPrompt />
      <ScrollToTop />
    </>
  )
}

function NativeWelcomeChromeGate({ children }: { children: ReactNode }) {
  const suppressNativeWelcomeChrome = useSuppressNativeWelcomeChrome()
  return suppressNativeWelcomeChrome ? null : <>{children}</>
}

function ThemeAndSearchLayout({ children }: { children: ReactNode }) {
  return (
    <ThemeProvider>
      <SearchProvider>{children}</SearchProvider>
    </ThemeProvider>
  )
}

function App() {
  return (
    <BrowserRouter>
      <ThemeAndSearchLayout>
        <PortfolioProvider>
          <DashboardProvider>
            <PageTracker />
            <FirstTouchAttributionManager />
            <FunnelMeasurementRelay />
            <AppChrome />
            <Toaster
              position="top-center"
              containerStyle={{
                top: 100, // Below the fixed navbar (navbar is at top-6 = 24px + ~60px height)
                zIndex: 11000,
              }}
              toastOptions={{
                duration: 4000,
                className:
                  'dark:bg-slate-800 dark:text-gray-100 !bg-white dark:!bg-slate-800 !shadow-lg !rounded-xl !border !border-gray-200 dark:!border-slate-700',
                style: {
                  background: 'var(--color-surface)',
                  color: 'var(--color-text-primary)',
                  padding: '12px 16px',
                  fontSize: '14px',
                  fontWeight: 500,
                  maxWidth: '400px',
                },
                success: {
                  duration: 3000,
                  iconTheme: {
                    primary: '#16a34a',
                    secondary: '#fff',
                  },
                  style: {
                    borderLeft: '4px solid #16a34a',
                  },
                },
                error: {
                  duration: 5000,
                  iconTheme: {
                    primary: '#dc2626',
                    secondary: '#fff',
                  },
                  style: {
                    borderLeft: '4px solid #dc2626',
                  },
                },
              }}
            />
            <span data-app-route-start hidden />
            <Routes>
              <Route
                path="/"
                element={
                  <MobileRootRedirect fallback={<PageLoader />}>
                    {withPageLoader(<LandingPage />)}
                  </MobileRootRedirect>
                }
              />
              <Route path="/signup" element={<SignUpPage />} />
              <Route path="/signup/success" element={<SignUpSuccessPage />} />
              <Route path="/signin" element={<SignInPage />} />
              <Route path="/reset-password" element={<PasswordResetPage />} />
              <Route path="/auth/callback" element={<AuthCallbackPage />} />
              <Route path="/faq" element={withPageLoader(<FAQPage />)} />
              <Route path="/contact" element={withPageLoader(<ContactPage />)} />
              <Route path="/about" element={withPageLoader(<AboutPage />)} />
              <Route
                path="/affiliate"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <AffiliatePage />
                  </Suspense>
                }
              />
              <Route
                path="/affiliate/dashboard"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AffiliateDashboardPage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/affiliate"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AdminAffiliatePage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/funnel"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AdminFunnelPage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/trial-feedback"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <TrialFeedbackPage />
                  </Suspense>
                }
              />
              <Route
                path="/inactive-feedback"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <InactiveFeedbackPage />
                  </Suspense>
                }
              />
              <Route
                path="/affiliate/:referralCode"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <AffiliateReferralRedirectPage />
                  </Suspense>
                }
              />

              <Route path="/privacy" element={withPageLoader(<PrivacyPage />)} />
              <Route path="/terms" element={withPageLoader(<TermsPage />)} />
              <Route path="/cookies" element={withPageLoader(<CookiePolicyPage />)} />
              <Route
                path="/how-to-track-options"
                element={withPageLoader(<HowToTrackOptionsPage />)}
              />
              <Route
                path="/csv-import"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <CSVImportGuidePage />
                  </Suspense>
                }
              />
              <Route
                path="/csv-import/:broker"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <CSVImportGuidePage />
                  </Suspense>
                }
              />
              <Route
                path="/options-paper-trading-simulator"
                element={withPageLoader(<OptionsPaperTradingSimulatorPage />)}
              />
              <Route path="/pricing" element={withPageLoader(<PricingPage />)} />
              <Route
                path="/trial-activation"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <TrialActivationPage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/upgrade-required"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <UpgradeRequiredPage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route path="/refund-policy" element={withPageLoader(<RefundPolicyPage />)} />
              <Route
                path="/security"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <SecurityPage />
                  </Suspense>
                }
              />
              <Route
                path="/broker-sync"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <BrokerSyncPage />
                  </Suspense>
                }
              />
              <Route
                path="/broker-sync/ibkr"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <IbkrBrokerSyncPage />
                  </Suspense>
                }
              />
              <Route
                path="/data-deletion"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <DataDeletionPage />
                  </Suspense>
                }
              />
              <Route
                path="/settings/broker-sync"
                element={<Navigate to="/settings?tab=broker_sync" replace />}
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <SettingsPage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <CoreAccessRoute>
                    <Suspense fallback={<PageLoader />}>
                      <DashboardPage />
                    </Suspense>
                  </CoreAccessRoute>
                }
              />
              <Route
                path="/portfolio"
                element={
                  <CoreAccessRoute>
                    <Suspense fallback={<PageLoader />}>
                      <PortfolioPage />
                    </Suspense>
                  </CoreAccessRoute>
                }
              />
              <Route
                path="/scenario-lab"
                element={
                  <CoreAccessRoute>
                    <Suspense fallback={<PageLoader />}>
                      <ScenarioLabPage />
                    </Suspense>
                  </CoreAccessRoute>
                }
              />
              <Route
                path="/trade-history"
                element={
                  <CoreAccessRoute>
                    <Suspense fallback={<PageLoader />}>
                      <TradeHistoryPage />
                    </Suspense>
                  </CoreAccessRoute>
                }
              />
              <Route
                path="/your-edge"
                element={
                  <CoreAccessRoute>
                    <Suspense fallback={<PageLoader />}>
                      <YourEdgePage />
                    </Suspense>
                  </CoreAccessRoute>
                }
              />
              <Route
                path="/screener"
                element={
                  <CoreAccessRoute>
                    <Suspense fallback={<PageLoader />}>
                      <ScreenerPage />
                    </Suspense>
                  </CoreAccessRoute>
                }
              />
              <Route
                path="/screener/:symbol"
                element={
                  <CoreAccessRoute>
                    <Suspense fallback={<PageLoader />}>
                      <ScreenerPage />
                    </Suspense>
                  </CoreAccessRoute>
                }
              />
              <Route
                path="/discovery"
                element={
                  <CoreAccessRoute>
                    <Suspense fallback={<PageLoader />}>
                      <DiscoveryPage />
                    </Suspense>
                  </CoreAccessRoute>
                }
              />
              <Route
                path="/feedback"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <FeedbackPage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              {/* Admin Scan Monitor - admin only */}
              <Route
                path="/admin/scans"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AdminScanMonitorPage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/email-campaigns"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AdminEmailCampaignsPage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/broker-sync"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AdminBrokerSyncPage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/features"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <FeatureHubPage />
                  </Suspense>
                }
              />
              <Route
                path="/features/:featureSlug"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ProductFeaturePage />
                  </Suspense>
                }
              />
              <Route
                path="/strategies/wheel-strategy"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <WheelStrategyPage />
                  </Suspense>
                }
              />
              <Route
                path="/strategies/covered-call"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <CoveredCallPage />
                  </Suspense>
                }
              />
              <Route
                path="/poor-mans-covered-call"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <PoorMansCoveredCallPage />
                  </Suspense>
                }
              />
              <Route
                path="/strategies/iron-condor"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <IronCondorPage />
                  </Suspense>
                }
              />
              <Route
                path="/strategies/married-put"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <MarriedPutPage />
                  </Suspense>
                }
              />
              <Route
                path="/strategies/bull-call-spread"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <BullCallSpreadPage />
                  </Suspense>
                }
              />
              <Route
                path="/strategies/bear-put-spread"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <BearPutSpreadPage />
                  </Suspense>
                }
              />
              <Route
                path="/strategies/protective-collar"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ProtectiveCollarPage />
                  </Suspense>
                }
              />
              <Route
                path="/strategies/long-straddle"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <LongStraddlePage />
                  </Suspense>
                }
              />
              <Route
                path="/strategies/long-strangle"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <LongStranglePage />
                  </Suspense>
                }
              />
              <Route
                path="/strategies/long-call-butterfly"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <LongCallButterflyPage />
                  </Suspense>
                }
              />
              <Route
                path="/strategies/iron-butterfly"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <IronButterflyPage />
                  </Suspense>
                }
              />
              <Route
                path="/strategies"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <StrategyHubPage />
                  </Suspense>
                }
              />
              <Route
                path="/strategies/:strategyId"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <StrategyHubPage />
                  </Suspense>
                }
              />
              <Route
                path="/compare"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ComparisonHubPage />
                  </Suspense>
                }
              />
              <Route
                path="/compare/:competitorId"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ComparisonPage />
                  </Suspense>
                }
              />
              <Route
                path="/glossary"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <GlossaryPage />
                  </Suspense>
                }
              />
              <Route
                path="/learn"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <LearningHubPage />
                  </Suspense>
                }
              />
              <Route
                path="/learning/screener"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ScreenerGuidePage />
                  </Suspense>
                }
              />
              <Route
                path="/learning/discovery"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <DiscoveryGuidePage />
                  </Suspense>
                }
              />
              <Route
                path="/learning/roll-assistant"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <RollAssistantGuidePage />
                  </Suspense>
                }
              />
              <Route
                path="/learning/scenario-lab"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ScenarioLabGuidePage />
                  </Suspense>
                }
              />
              <Route
                path="/blog"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <BlogPage />
                  </Suspense>
                }
              />
              <Route
                path="/blog/:slug"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <BlogPostPage />
                  </Suspense>
                }
              />
              {/* SEO Pages */}
              <Route
                path="/tools/options-profit-calculator"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <OptionsProfitCalculatorPage />
                  </Suspense>
                }
              />
              <Route
                path="/glossary/theta"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <GlossaryThetaPage />
                  </Suspense>
                }
              />
              {/* Public demo page: no auth required */}
              <Route
                path="/demo"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <DemoPage />
                  </Suspense>
                }
              />
              {/* Public email unsubscribe / manage-preferences page: no auth required */}
              <Route
                path="/email/unsubscribe"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <UnsubscribePage />
                  </Suspense>
                }
              />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            <span data-app-route-end hidden />
            <NativeWelcomeChromeGate>
              {/* Cookie Consent UI */}
              <CookieBanner />
              <CookiePreferencesModal />
              <SecondChanceConsentManager />
            </NativeWelcomeChromeGate>
          </DashboardProvider>
        </PortfolioProvider>
      </ThemeAndSearchLayout>
    </BrowserRouter>
  )
}

export default App
